import { CalendarDays, FileText } from "lucide-react"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import type { TaskDetail } from "@/hooks/useWidgetTabs"
import { Badge } from "@/components/ui/badge"
import TaskTimeline from "@/components/chat/TaskTimeline"
import { TaskSummaryContent } from "@/components/chat/TaskSummaryContent"

/**
 * Task details — the widget-panel tool every Agent-thread task widget opens.
 * Where the thread shows momentary snapshots, this is where the whole task
 * lives: status + on-time badge, and — for a finished task — the same
 * at-a-glance figures as the Task Summary widget, plus the order's documents
 * and its full stage history (behind / now / ahead).
 */
export type TaskDetailsToolProps = {
  detail: TaskDetail
}

const formatHm = (totalMinutes: number): string => {
  const abs = Math.abs(Math.round(totalMinutes))
  const h = Math.floor(abs / 60)
  const m = abs % 60
  if (h === 0) return `${m} min`
  if (m === 0) return `${h} h`
  return `${h} h ${m} min`
}

/** Green "Na czas" / red "Opóźnienie +Xh" / green "Przed czasem −Xh". */
const scheduleBadge = (
  minutes: number | undefined,
  t: TFunction,
): { variant: "positive" | "error"; label: string } => {
  const d = minutes == null ? 0 : Math.round(minutes)
  if (d === 0) return { variant: "positive", label: t("chat.task.onTime") }
  return d > 0
    ? {
        variant: "error",
        label: t("chat.task.late", { duration: formatHm(d) }),
      }
    : {
        variant: "positive",
        label: t("chat.task.early", { duration: formatHm(d) }),
      }
}

export const TaskDetailsTool = ({ detail }: TaskDetailsToolProps) => {
  const { t } = useTranslation()
  const title = detail.routeLabel ?? t("chat.task.orderFallback")
  const done = detail.taskStatus === "done"
  const schedule = scheduleBadge(detail.scheduleDeltaMinutes, t)
  const documents = detail.documents ?? []

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border px-3.5 py-3">
        <div className="flex items-start justify-between gap-2">
          <span className="truncate text-sm font-semibold text-foreground">
            {title}
          </span>
          <Badge variant={done ? "neutral" : "info"} className="shrink-0">
            {done ? t("chat.task.done") : t("chat.task.ongoing")}
          </Badge>
        </div>
        {detail.dateLabel && (
          <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" />
            {detail.dateLabel}
          </div>
        )}
        <div className="mt-2">
          <Badge variant={schedule.variant}>{schedule.label}</Badge>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {detail.summary && (
          <div className="border-b border-border p-4">
            <TaskSummaryContent {...detail.summary} />
          </div>
        )}

        {documents.length > 0 && (
          <div className="border-b border-border p-4">
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {t("chat.task.documents")}
            </div>
            <ul className="space-y-1.5">
              {documents.map((doc, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm text-left text-sm text-base-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <FileText
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span className="min-w-0 truncate">{doc.fileName}</span>
                    {doc.kind && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {doc.kind}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {detail.steps.length > 0 && (
          <div className="p-4">
            <TaskTimeline steps={detail.steps} />
          </div>
        )}
      </div>
    </div>
  )
}

export default TaskDetailsTool
